import { useState, useRef, useCallback, useEffect } from "react";

type RecordingStatus = "idle" | "recording" | "paused";

interface AudioInputDevice {
  deviceId: string;
  label: string;
}

interface UseAudioRecorderResult {
  status: RecordingStatus;
  timer: number;
  hasPermission: boolean;
  devices: AudioInputDevice[];
  selectedDeviceId: string;
  analyserNode: AnalyserNode | null;
  setSelectedDeviceId: (id: string) => void;
  requestPermission: () => Promise<void>;
  startRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => Promise<AudioBuffer | null>;
  resetRecording: () => void;
}

export function useAudioRecorder(): UseAudioRecorderResult {
  const [status, setStatus] = useState<RecordingStatus>("idle");
  const [timer, setTimer] = useState(0);
  const [hasPermission, setHasPermission] = useState(false);
  const [devices, setDevices] = useState<AudioInputDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceIdRaw] = useState(() => {
    return localStorage.getItem("audioRecorder.deviceId") || "";
  });

  const setSelectedDeviceId = useCallback((id: string) => {
    setSelectedDeviceIdRaw(id);
    localStorage.setItem("audioRecorder.deviceId", id);
  }, []);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopResolveRef = useRef<((buffer: AudioBuffer | null) => void) | null>(null);

  const enumerateDevices = useCallback(async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = allDevices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone (${device.deviceId.slice(0, 5)}...)`,
        }));
      setDevices(audioInputDevices);

      if (audioInputDevices.length > 0) {
        const saved = localStorage.getItem("audioRecorder.deviceId");
        const savedDevice = saved
          ? audioInputDevices.find((d) => d.deviceId === saved)
          : null;
        const defaultDevice =
          savedDevice ||
          audioInputDevices.find((d) => d.deviceId === "default") ||
          audioInputDevices[0];
        setSelectedDeviceIdRaw(defaultDevice.deviceId);
      }
    } catch (error) {
      console.error("Error enumerating devices:", error);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      await enumerateDevices();
    } catch (error) {
      console.error("Error requesting permission:", error);
      setHasPermission(false);
    }
  }, [enumerateDevices]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
        },
      });
      streamRef.current = stream;

      // Create AudioContext + AnalyserNode for visualization
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      setAnalyserNode(analyser);

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        try {
          const arrayBuffer = await audioBlob.arrayBuffer();
          // Use a fresh context for decoding since the recording one may be closed
          const decodeCtx = new AudioContext();
          const audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer);
          await decodeCtx.close();
          stopResolveRef.current?.(audioBuffer);
        } catch (error) {
          console.error("Error decoding audio:", error);
          stopResolveRef.current?.(null);
        }
        stopResolveRef.current = null;
      };

      mediaRecorder.start();
      setStatus("recording");
      setTimer(0);
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  }, [selectedDeviceId]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === "recording") {
      mediaRecorderRef.current.pause();
      setStatus("paused");
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [status]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === "paused") {
      mediaRecorderRef.current.resume();
      setStatus("recording");
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    }
  }, [status]);

  const stopRecording = useCallback((): Promise<AudioBuffer | null> => {
    return new Promise((resolve) => {
      if (
        mediaRecorderRef.current &&
        (status === "recording" || status === "paused")
      ) {
        stopResolveRef.current = resolve;
        mediaRecorderRef.current.stop();
        setStatus("idle");
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      } else {
        resolve(null);
      }
    });
  }, [status]);

  const resetRecording = useCallback(() => {
    setStatus("idle");
    setTimer(0);
    setAnalyserNode(null);

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch {}
      }
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    audioChunksRef.current = [];
    stopResolveRef.current = null;
  }, []);

  // Check initial permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        setHasPermission(permissionStatus.state === "granted");

        permissionStatus.addEventListener("change", () => {
          setHasPermission(permissionStatus.state === "granted");
          if (permissionStatus.state !== "granted" && mediaRecorderRef.current) {
            resetRecording();
          }
        });

        if (permissionStatus.state === "granted") {
          await enumerateDevices();
        }
      } catch (error) {
        console.error("Error checking permission:", error);
        setHasPermission(false);
      }
    };
    checkPermission();
  }, [enumerateDevices, resetRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  return {
    status,
    timer,
    hasPermission,
    devices,
    selectedDeviceId,
    analyserNode,
    setSelectedDeviceId,
    requestPermission,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
  };
}
